/* eslint-disable no-underscore-dangle */

/**
 * A component to invoke asynchronous requests and cache the responses for some period of time such that subsequent
 * requests avoid hitting the server.
 *
 * Once the initial request has been completed, subsequent requests will return the cached result
 * until such time the cache expires. The next request after expiration will fetch an updated response and update the
 * cache. Repeat ad infinitum.
 * Users of this object simply call response() and need not worry about the above.
 * The specifics of the asynchronous request are not important to this component, only that the request function return
 * a promise.
 *
 * This object is not coupled to any specific cache mechanism. Only that the cache support an interface of 3 functions:
 *  1. setItem(key, value) - store the given value under the given key
 *  2. getItem(key) - return the value associated with a given key
 *  3. removeItem(key) - remove the value associated with a given key
 *
 * Example scenario of how to use this component:
 *   We want to reduce the amount of server queries to /API/Foo/3 because it rarely changes.
 *   We leverage CachedAsyncRequest to access a response. We can access the response as often as we like but internally
 *   it makes an initial call and returns a cached copy of the response that invalidates every 5 minutes.
 *
 * //This is our asynchronous request function. It returns a promise
 * function getMyData() { return lsCloud.getJSONFromAPI(/API/Foo/3); }
 *
 * //Initialize a cached query of /API/Foo/3 endpoint that invalidates every 5 minutes
 * var expires = 5 * 60 * 1000; //ms
 * var theCache = new merchantos.cached_request.MemoryCache();
 * //alternatively
 * //var theCache = merchantos.cached_request.SessionStorageCache;
 * var request = new merchantos.cached_request.CachedAsyncRequest(getMyData, expires, theCache);
 *
 * ....later....
 *
 * //first call
 * var key = '3'; //how to identify the results
 * var promiseResult = request.getResponse(key); //->no cache yet, so makes API call to server. Result is cached
 *
 * //next call is made 3 minutes later...
 * var promiseResult2 = request.getResponse(key); //cache has not expired, so cached response is returned.
 *
 * //next call is made 4 minutes after above
 * var promiseResult3 = request.getResponse(key); //cache has expired (7 minutes). Makes API call and updates cache.
 *
 *
 * Copyright 2015 LightSpeed Retail. All rights reserved.
 * Created by acastruc on 2015-08-24.
 */

class CachedAsyncRequest implements CachedAsyncRequest {
  _cacheExpiration: number;

  _cache: SessionStorageCache | MemoryCache;

  _requestFunc: (...args: any[]) => JQuery.Promise<any>;

  /**
   * Construct a cached request object. Requests are made on demand only via the response() method.
   * @param expiration {Number} milliseconds after which the cache is considered invalid
   * @param requestFunc {Function} that performs an asynchronous operation and returns a {Promise} for the result
   * @param dataStore {Object} where results are to be stored. Must support 3 functions: setItem(key,value),
   * 					getItem(key), removeItem(key)
   * @constructor
   */
  constructor(
    requestFunc: (...args: any[]) => JQuery.Promise<any>,
    expiration: number,
    dataStore: SessionStorageCache | MemoryCache
  ) {
    this._cacheExpiration = expiration;
    this._requestFunc = requestFunc;
    this._cache = dataStore;
  }

  /**
   * Return the promise that will resolve with the request response.
   * This may be a cached copy of the response if the given refresh interval has not passed (aka cache not
   * invalidated).
   * If the cached copy is invalidated then kick off a new request and return that promise;
   * @param key {String} key under which to store the results of the async-request
   * @return {Promise} the response for the async request this object represents
   */
  getResponse = (key: string) => {
    // look it up in cache
    let response;
    const cachedObj = this._cache.getItem(key);
    if (this.isCacheValid(cachedObj)) {
      const data = cachedObj.data;
      const deferred = $.Deferred();
      deferred.resolve(data);
      response = deferred.promise();
    } else {
      // we don't have a cached response OR it's expired
      response = this.update(key);
    }
    return response;
  };

  /**
   * Return whether the cached result is generally valid and has not existed longer than the expiration specified.
   * @returns {boolean}
   */
  isCacheValid = (cachedObj: any): boolean => {
    return (
      cachedObj &&
      cachedObj.data &&
      cachedObj.timeStamp &&
      !isNaN(Date.parse(cachedObj.timeStamp)) &&
      // @ts-expect-error ts-migrate(2362) FIXME: The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
      new Date() - new Date(cachedObj.timeStamp) < this._cacheExpiration
    );
  };

  /**
   * Force a fresh request and update the cache under the given key
   * @param key {String} to associate with the updated results
   * @return {Promise} the updated response
   */
  update = (key: string): JQuery.Promise<void> => {
    this._cache.removeItem(key);

    const timeStamp = new Date();
    const response = this._requestFunc(key);
    response.done(data => this._cache.setItem(key, { timeStamp, data }));
    return response;
  };
}

/**
 * Implementation of an in-memory cache that can be used as storage
 * @constructor
 */
class MemoryCache implements MemoryCache {
  _data: { [key: string]: any };

  constructor() {
    this._data = {};
  }

  setItem = (key: string, value: any): void => {
    this._data[key] = value;
  };

  getItem = (key: string): any => {
    return this._data[key];
  };

  removeItem = (key: string): void => {
    delete this._data[key];
  };
}

class SessionStorageCache implements SessionStorageCache {
  /**
   * Implementation of a cache for async requests that is backed by window.sessionStorage
   */

  setItem = (key: string, value: any) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      window.merchantos.error(err, 'Failed to write to sessionStorage: ');
    }
  };

  getItem = (key: string) => {
    try {
      const value = sessionStorage.getItem(key);
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
      return JSON.parse(value);
    } catch (err) {
      window.merchantos.error(err, 'Failed to read from sessionStorage: ');
      return null;
    }
  };

  removeItem = (key: string) => {
    try {
      sessionStorage.removeItem(key);
    } catch (err) {
      window.merchantos.error(err, 'Failed to remove from sessionStorage: ');
    }
  };
}

window.merchantos.cached_request = {
  CachedAsyncRequest,
  MemoryCache,
  SessionStorageCache: new SessionStorageCache(),
};
